import { useQuery, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { UseFormRegister, useForm } from "react-hook-form";

import { addCategory, editCategory, getCategories, getSingleCategory } from "../../core/category.core";
import localeCore from "../../core/locale.core";
import { useTranslation } from "../../core/translation.core";
import { Category, CategoryFormData } from "../../types/category";
import { LocaleTextAresDefaultLocale, LocalInputDefaultLocale } from "../locale/locale.comp";
import { EditorSection, SubmitPanel, Title } from "../reuseables/editor.comp";
import { EditingMode, FancyCheckbox, LabelAndInput } from "../reuseables/form.comp";
import { selectCss } from "../reuseables/native.comp";
import ExcludePlacementEditor from "../common/excludePlacementEditor.comp";
import MediaSelector, { MediaSelectorMode } from "../media/mediaSelector.comp";
import InfoBanner from "../reuseables/infoBanner.comp";

export default function CategoryEditor({ editingMode, categoryId }: { editingMode: EditingMode; categoryId: string }) {
  const router = useRouter();
  const methods = useForm<CategoryFormData>({
    defaultValues: {
      isRewardRedeemable: true,
    },
  });
  const queryClient = useQueryClient();
  const [submitText, setSubmitText] = useState("Submit");

  const { t, msg } = useTranslation();

  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });

  useEffect(() => {
    if (editingMode === EditingMode.edit) {
      fetchCategory();
    }
    // eslint-disable-next-line
  }, [categoryId, editingMode]);

  async function fetchCategory() {
    if (categoryId === undefined) return;
    if (editingMode !== EditingMode.edit) return;

    const data = await getSingleCategory(categoryId as string);
    methods.reset(convertCategoryToForm(data));
  }

  const onSubmit = async (data: CategoryFormData) => {
    setSubmitText("Submitting...");
    switch (editingMode) {
      case EditingMode.create:
        await addCategory(data);
        break;
      case EditingMode.edit:
        await editCategory(data);
        break;
      default:
        break;
    }

    queryClient.invalidateQueries({ queryKey: ["categories"] });
    setSubmitText("Success");
    router.push("/categories");
  };

  function getSubmitText() {
    switch (submitText) {
      case "Submitting...":
        return t(msg.form.submitting);
      case "Success":
        return t(msg.form.success);
      default:
        return t(msg.form.submit);
    }
  }

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className="pb-6">
      <EditorSection>
        <Title>{t(msg.product.generalInfo)}</Title>
        <div className="space-y-4">
          <LabelAndInput
            register={methods.register}
            fieldName="code"
            label={t(msg.category.code)}
            registerOptions={{ disabled: editingMode === EditingMode.edit }}
            topLabelCss="block"
          />
          <LocalInputDefaultLocale label={t(msg.category.name)} register={methods.register} fieldName="name" />
          <LocaleTextAresDefaultLocale label={t(msg.category.description)} register={methods.register} fieldName="description" />
          <LocalInputDefaultLocale label={t(msg.category.slug)} register={methods.register} fieldName="slug" required />
          <ParentOptions register={methods.register} categories={categories} />
          <div className="text-xs flex flex-col gap-4">
            <FancyCheckbox value={true} register={methods.register("isPromotion")}>
              {t(msg.category.isPromotion)}
            </FancyCheckbox>
          </div>
          {editingMode === EditingMode.edit && (
            <Link className="block underline my-4" href={`/discount/addToGroup?catId=${categoryId}`}>
              Add to Discount Group
            </Link>
          )}
        </div>
      </EditorSection>
      <EditorSection>
        <Title>{t(msg.reward.reward)}</Title>
        <div className="flex flex-col gap-4">
          <LabelAndInput
            label={t(msg.shop.rewardPayout)}
            register={methods.register}
            fieldName="rewardPayout"
            type="number"
            step="any"
            infoComponents={<InfoBanner message={t(msg.category.rewardPayoutInfo)} />}
          />
          <div className="text-xs flex flex-col gap-4">
            <label>
              <span className={`text-xs font-semibold mr-2`}>{t(msg.reward.isRewardRedeemable)}</span>
              <input type="checkbox" {...methods.register("isRewardRedeemable")} />
            </label>
          </div>
        </div>
      </EditorSection>
      <EditorSection>
        <Title>{t(msg.product.productVisibility)}</Title>
        <div>
          <ExcludePlacementEditor methods={methods} />
        </div>
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.media.media)}</Title>
        <div>
          <MediaSelector
            id="feature-image"
            mode={MediaSelectorMode.single}
            label={t(msg.product.featuredImage)}
            defaultSelectedIds={methods.getValues("featuredMediaId") === undefined ? [] : [methods.getValues("featuredMediaId")!]}
            className="mh-10"
            onChanged={(ids) => {
              if (ids === undefined || ids.length === 0) {
                methods.setValue("featuredMediaId", undefined);
              } else {
                methods.setValue("featuredMediaId", ids[0]);
              }
            }}
          />

          <MediaSelector
            id="icon-image"
            mode={MediaSelectorMode.single}
            label="Icon Image"
            defaultSelectedIds={methods.getValues("iconMediaId") === undefined ? [] : [methods.getValues("iconMediaId")!]}
            className="mh-10 mt-4"
            onChanged={(ids) => {
              if (ids === undefined || ids.length === 0) {
                methods.setValue("iconMediaId", undefined);
              } else {
                methods.setValue("iconMediaId", ids[0]);
              }
            }}
          />
        </div>
      </EditorSection>

      <SubmitPanel>
        <button onClick={() => router.push("/categories")}>{t(msg.form.cancel)}</button>
        <input type="submit" value={getSubmitText()} onBlur={() => setSubmitText("Submit")} />
      </SubmitPanel>
    </form>
  );
}

interface ParentOptionsProps {
  register: UseFormRegister<CategoryFormData>;
  categories?: Category[];
}

const ParentOptions = ({ register, categories }: ParentOptionsProps) => {
  const { t, msg } = useTranslation();
  const router = useRouter();

  return (
    <label>
      <span className="block text-xs font-semibold mb-2">{t(msg.category.addParent)}</span>
      <select className={selectCss + " max-w-[650px]"} {...register("parentId")}>
        <option></option>
        {categories?.map((c: Category) => (
          <option key={c.id} value={c.id}>
            {localeCore.getLocaleValue(c.name, router.locale)} ({localeCore.getLocaleValue(c.slug, router.locale)})
          </option>
        ))}
      </select>
    </label>
  );
};

function convertCategoryToForm(category: Category): CategoryFormData {
  return {
    id: category.id,
    code: category.code,
    name: category.name,
    description: category.description,
    slug: category.slug,
    parentId: category.parentId,
    rewardPayout: category.rewardPayout,
    isRewardRedeemable: category.isRewardRedeemable,
    excludePlacement: category.excludePlacement,
    excludePlacementLocation: category.excludePlacementLocation,
    excludePlacementRetailIds: category.excludePlacementRetailIds,
    featuredMediaId: category.featuredMedia?.id,
    iconMediaId: category.iconMedia?.id,
    isPromotion: category.isPromotion,
  };
}
